import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Tabs,
  Tab,
  Avatar,
  Badge,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemAvatar,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Checkbox
} from '@mui/material';
import {
  Save,
  Edit,
  Delete,
  Add,
  Person,
  Security,
  Notifications,
  Language,
  Backup,
  Cloud,
  Devices,
  Palette,
  TextFields,
  Visibility,
  VisibilityOff,
  Lock,
  LockOpen,
  ExpandMore,
  Email,
  Phone,
  LocationOn,
  Business,
  Group,
  AdminPanelSettings,
  VerifiedUser,
  PersonAdd,
  Key,
  History,
  Logout,
  Refresh,
  Download,
  Upload
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [userSettings, setUserSettings] = useState({
    username: 'admin',
    email: 'admin@mineplanning.com',
    firstName: 'System',
    lastName: 'Administrator',
    phone: '+27 11 123 4567',
    role: 'admin',
    department: 'Management',
    language: 'en',
    theme: 'light',
    notifications: {
      email: true,
      sms: false,
      push: true,
      dailyReports: true,
      alerts: true
    }
  });

  const [companySettings, setCompanySettings] = useState({
    companyName: 'Mine Planning Co.',
    address: '123 Mining Street, Johannesburg',
    contactEmail: 'info@mineplanning.co.za',
    contactPhone: '+27 11 987 6543',
    website: 'www.mineplanning.co.za',
    currency: 'ZAR',
    timezone: 'Africa/Johannesburg',
    workingHours: '07:00 - 18:00',
    dieselPrice: 17.9,
    defaultDensity: {
      soft: 1.8,
      hard: 2.6,
      coal: 1.5
    }
  });

  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    autoBackup: true,
    backupFrequency: 'daily',
    dataRetention: 365,
    apiEnabled: true,
    auditLogging: true,
    twoFactorAuth: false,
    sessionTimeout: 30
  });

  const [users, setUsers] = useState([
    { id: 1, username: 'admin', email: 'admin@mineplanning.com', role: 'admin', status: 'active', lastLogin: '2024-01-30 14:30' },
    { id: 2, username: 'manager1', email: 'manager@mine.com', role: 'manager', status: 'active', lastLogin: '2024-01-30 10:15' },
    { id: 3, username: 'operator1', email: 'operator@mine.com', role: 'operator', status: 'active', lastLogin: '2024-01-30 08:45' },
    { id: 4, username: 'supervisor1', email: 'supervisor@mine.com', role: 'supervisor', status: 'inactive', lastLogin: '2024-01-28 16:20' },
    { id: 5, username: 'viewer1', email: 'viewer@mine.com', role: 'viewer', status: 'active', lastLogin: '2024-01-29 11:30' }
  ]);

  const [roles, setRoles] = useState([
    { id: 1, name: 'admin', description: 'Full system access', permissions: ['all'] },
    { id: 2, name: 'manager', description: 'Management access', permissions: ['view', 'edit', 'approve'] },
    { id: 3, name: 'supervisor', description: 'Supervisory access', permissions: ['view', 'edit'] },
    { id: 4, name: 'operator', description: 'Operator access', permissions: ['view', 'add'] },
    { id: 5, name: 'viewer', description: 'View-only access', permissions: ['view'] }
  ]);

  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'operator',
    department: ''
  });
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: []
  });

  const handleSaveSettings = () => {
    // Save all settings to backend
    console.log('Saving settings:', {
      userSettings,
      companySettings,
      systemSettings
    });
    alert('Settings saved successfully!');
  };

  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setUserForm({
        username: user.username,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role,
        department: user.department || ''
      });
    } else {
      setSelectedUser(null);
      setUserForm({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        role: 'operator',
        department: ''
      });
    }
    setOpenUserDialog(true);
  };

  const handleOpenRoleDialog = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setRoleForm({
        name: role.name,
        description: role.description,
        permissions: role.permissions
      });
    } else {
      setSelectedRole(null);
      setRoleForm({
        name: '',
        description: '',
        permissions: []
      });
    }
    setOpenRoleDialog(true);
  };

  const handleSaveUser = () => {
    if (selectedUser) {
      // Update user
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id ? { ...selectedUser, ...userForm } : user
      ));
    } else {
      // Add new user
      const newUser = {
        id: users.length + 1,
        ...userForm,
        status: 'active',
        lastLogin: new Date().toISOString().split('T')[0] + ' 00:00'
      };
      setUsers(prev => [...prev, newUser]);
    }
    setOpenUserDialog(false);
  };

  const handleSaveRole = () => {
    if (selectedRole) {
      // Update role
      setRoles(prev => prev.map(role => 
        role.id === selectedRole.id ? { ...selectedRole, ...roleForm } : role
      ));
    } else {
      // Add new role
      const newRole = {
        id: roles.length + 1,
        ...roleForm
      };
      setRoles(prev => [...prev, newRole]);
    }
    setOpenRoleDialog(false);
  };

  const handleDeleteUser = (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(prev => prev.filter(user => user.id !== id));
    }
  };

  const handleDeleteRole = (id) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      setRoles(prev => prev.filter(role => role.id !== id));
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'supervisor': return 'info';
      case 'operator': return 'primary';
      case 'viewer': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Save />}
          onClick={handleSaveSettings}
        >
          Save All Changes
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="User Profile" icon={<Person />} />
          <Tab label="Company Settings" icon={<Business />} />
          <Tab label="System Configuration" icon={<Devices />} />
          <Tab label="User Management" icon={<Group />} />
          <Tab label="Role Management" icon={<AdminPanelSettings />} />
          <Tab label="Security" icon={<Security />} />
          <Tab label="Backup & Restore" icon={<Backup />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {/* User Profile Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 1 }} /> Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={userSettings.firstName}
                      onChange={(e) => setUserSettings({...userSettings, firstName: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={userSettings.lastName}
                      onChange={(e) => setUserSettings({...userSettings, lastName: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={userSettings.email}
                      onChange={(e) => setUserSettings({...userSettings, email: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={userSettings.phone}
                      onChange={(e) => setUserSettings({...userSettings, phone: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={userSettings.department}
                      onChange={(e) => setUserSettings({...userSettings, department: e.target.value})}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Palette sx={{ mr: 1 }} /> Preferences
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={userSettings.language}
                        label="Language"
                        onChange={(e) => setUserSettings({...userSettings, language: e.target.value})}
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="af">Afrikaans</MenuItem>
                        <MenuItem value="zu">Zulu</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Theme</InputLabel>
                      <Select
                        value={userSettings.theme}
                        label="Theme"
                        onChange={(e) => setUserSettings({...userSettings, theme: e.target.value})}
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                        <MenuItem value="auto">Auto</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Notifications
                    </Typography>
                    {Object.entries(userSettings.notifications).map(([key, value]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Switch
                            checked={value}
                            onChange={(e) => setUserSettings({
                              ...userSettings,
                              notifications: {
                                ...userSettings.notifications,
                                [key]: e.target.checked
                              }
                            })}
                          />
                        }
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      />
                    ))}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Security sx={{ mr: 1 }} /> Security
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" startIcon={<Save />}>
                      Update Password
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Company Settings Tab */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ mr: 1 }} /> Company Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      value={companySettings.companyName}
                      onChange={(e) => setCompanySettings({...companySettings, companyName: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={companySettings.address}
                      onChange={(e) => setCompanySettings({...companySettings, address: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      type="email"
                      value={companySettings.contactEmail}
                      onChange={(e) => setCompanySettings({...companySettings, contactEmail: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={companySettings.contactPhone}
                      onChange={(e) => setCompanySettings({...companySettings, contactPhone: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={companySettings.website}
                      onChange={(e) => setCompanySettings({...companySettings, website: e.target.value})}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Language sx={{ mr: 1 }} /> Operational Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={companySettings.currency}
                        label="Currency"
                        onChange={(e) => setCompanySettings({...companySettings, currency: e.target.value})}
                      >
                        <MenuItem value="ZAR">ZAR (South African Rand)</MenuItem>
                        <MenuItem value="USD">USD (US Dollar)</MenuItem>
                        <MenuItem value="EUR">EUR (Euro)</MenuItem>
                        <MenuItem value="GBP">GBP (British Pound)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={companySettings.timezone}
                        label="Timezone"
                        onChange={(e) => setCompanySettings({...companySettings, timezone: e.target.value})}
                      >
                        <MenuItem value="Africa/Johannesburg">Africa/Johannesburg</MenuItem>
                        <MenuItem value="UTC">UTC</MenuItem>
                        <MenuItem value="America/New_York">America/New_York</MenuItem>
                        <MenuItem value="Europe/London">Europe/London</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Working Hours"
                      value={companySettings.workingHours}
                      onChange={(e) => setCompanySettings({...companySettings, workingHours: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Diesel Price (R/L)"
                      type="number"
                      value={companySettings.dieselPrice}
                      onChange={(e) => setCompanySettings({...companySettings, dieselPrice: parseFloat(e.target.value) || 0})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Default Material Densities (ton/m³)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Soft Soil"
                          type="number"
                          value={companySettings.defaultDensity.soft}
                          onChange={(e) => setCompanySettings({
                            ...companySettings,
                            defaultDensity: {
                              ...companySettings.defaultDensity,
                              soft: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Hard Soil"
                          type="number"
                          value={companySettings.defaultDensity.hard}
                          onChange={(e) => setCompanySettings({
                            ...companySettings,
                            defaultDensity: {
                              ...companySettings.defaultDensity,
                              hard: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Coal"
                          type="number"
                          value={companySettings.defaultDensity.coal}
                          onChange={(e) => setCompanySettings({
                            ...companySettings,
                            defaultDensity: {
                              ...companySettings.defaultDensity,
                              coal: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* System Configuration Tab */}
        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Devices sx={{ mr: 1 }} /> System Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.maintenanceMode}
                          onChange={(e) => setSystemSettings({...systemSettings, maintenanceMode: e.target.checked})}
                        />
                      }
                      label="Maintenance Mode"
                    />
                    <Typography variant="caption" color="textSecondary" display="block">
                      When enabled, only administrators can access the system
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.autoBackup}
                          onChange={(e) => setSystemSettings({...systemSettings, autoBackup: e.target.checked})}
                        />
                      }
                      label="Automatic Backups"
                    />
                  </Grid>
                  {systemSettings.autoBackup && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Backup Frequency</InputLabel>
                        <Select
                          value={systemSettings.backupFrequency}
                          label="Backup Frequency"
                          onChange={(e) => setSystemSettings({...systemSettings, backupFrequency: e.target.value})}
                        >
                          <MenuItem value="hourly">Hourly</MenuItem>
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Data Retention (days)"
                      type="number"
                      value={systemSettings.dataRetention}
                      onChange={(e) => setSystemSettings({...systemSettings, dataRetention: parseInt(e.target.value) || 0})}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Session Timeout (minutes)"
                      type="number"
                      value={systemSettings.sessionTimeout}
                      onChange={(e) => setSystemSettings({...systemSettings, sessionTimeout: parseInt(e.target.value) || 0})}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Cloud sx={{ mr: 1 }} /> API & Integration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.apiEnabled}
                          onChange={(e) => setSystemSettings({...systemSettings, apiEnabled: e.target.checked})}
                        />
                      }
                      label="Enable API Access"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.auditLogging}
                          onChange={(e) => setSystemSettings({...systemSettings, auditLogging: e.target.checked})}
                        />
                      }
                      label="Audit Logging"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.twoFactorAuth}
                          onChange={(e) => setSystemSettings({...systemSettings, twoFactorAuth: e.target.checked})}
                        />
                      }
                      label="Two-Factor Authentication"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" startIcon={<Refresh />}>
                      Regenerate API Keys
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* User Management Tab */}
        {activeTab === 3 && (
          <Box>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">User Management</Typography>
                <Button 
                  variant="contained" 
                  startIcon={<PersonAdd />}
                  onClick={() => handleOpenUserDialog()}
                >
                  Add New User
                </Button>
              </Box>
            </Paper>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2 }}>
                            {user.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">{user.username}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.role} 
                          size="small" 
                          color={getRoleColor(user.role)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.status} 
                          size="small" 
                          color={getStatusColor(user.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{user.lastLogin}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenUserDialog(user)}
                            sx={{ mr: 1 }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset Password">
                          <IconButton size="small" sx={{ mr: 1 }}>
                            <Key fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Role Management Tab */}
        {activeTab === 4 && (
          <Box>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Role Management</Typography>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={() => handleOpenRoleDialog()}
                >
                  Add New Role
                </Button>
              </Box>
            </Paper>
            <Grid container spacing={3}>
              {roles.map((role) => (
                <Grid item xs={12} md={6} key={role.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">{role.name}</Typography>
                        <Box>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenRoleDialog(role)}
                              sx={{ mr: 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteRole(role.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {role.description}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Permissions:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {role.permissions.map((perm, index) => (
                          <Chip 
                            key={index}
                            label={perm}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Security Tab */}
        {activeTab === 5 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Security sx={{ mr: 1 }} /> Security Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={systemSettings.twoFactorAuth} />}
                      label="Enable Two-Factor Authentication"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Session Timeout"
                      type="number"
                      value={systemSettings.sessionTimeout}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={systemSettings.auditLogging} />}
                      label="Enable Audit Logging"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" startIcon={<History />}>
                      View Audit Logs
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <VerifiedUser sx={{ mr: 1 }} /> Password Policy
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Minimum Password Length"
                      type="number"
                      defaultValue={8}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="Require Special Characters"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="Require Numbers"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="Require Uppercase Letters"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password Expiry (days)"
                      type="number"
                      defaultValue={90}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Backup & Restore Tab */}
        {activeTab === 6 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Backup sx={{ mr: 1 }} /> Backup Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.autoBackup}
                          onChange={(e) => setSystemSettings({...systemSettings, autoBackup: e.target.checked})}
                        />
                      }
                      label="Automatic Backups"
                    />
                  </Grid>
                  {systemSettings.autoBackup && (
                    <>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Backup Frequency</InputLabel>
                          <Select
                            value={systemSettings.backupFrequency}
                            label="Backup Frequency"
                            onChange={(e) => setSystemSettings({...systemSettings, backupFrequency: e.target.value})}
                          >
                            <MenuItem value="hourly">Hourly</MenuItem>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Backup Retention (days)"
                          type="number"
                          value={systemSettings.dataRetention}
                          onChange={(e) => setSystemSettings({...systemSettings, dataRetention: parseInt(e.target.value) || 0})}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Button variant="contained" startIcon={<Download />} fullWidth>
                      Create Manual Backup
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Cloud sx={{ mr: 1 }} /> Restore & Import
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button variant="outlined" startIcon={<Upload />} fullWidth sx={{ mb: 2 }}>
                      Upload Backup File
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recent Backups
                    </Typography>
                    <List dense>
                      {['2024-01-30_0200.backup', '2024-01-29_0200.backup', '2024-01-28_0200.backup'].map((file, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Backup />
                          </ListItemIcon>
                          <ListItemText primary={file} secondary="2.4 GB" />
                          <ListItemSecondaryAction>
                            <Tooltip title="Restore">
                              <IconButton edge="end" size="small">
                                <Refresh />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* User Dialog */}
      <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={userForm.username}
                onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={userForm.firstName}
                onChange={(e) => setUserForm({...userForm, firstName: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={userForm.lastName}
                onChange={(e) => setUserForm({...userForm, lastName: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={userForm.role}
                  label="Role"
                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="supervisor">Supervisor</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={userForm.department}
                onChange={(e) => setUserForm({...userForm, department: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {selectedUser ? 'Update' : 'Create'} User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedRole ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                value={roleForm.name}
                onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={roleForm.description}
                onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Permissions
              </Typography>
              <Grid container spacing={1}>
                {['view', 'add', 'edit', 'delete', 'approve', 'export', 'import', 'admin'].map((perm) => (
                  <Grid item xs={6} key={perm}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={roleForm.permissions.includes(perm)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoleForm({...roleForm, permissions: [...roleForm.permissions, perm]});
                            } else {
                              setRoleForm({...roleForm, permissions: roleForm.permissions.filter(p => p !== perm)});
                            }
                          }}
                        />
                      }
                      label={perm.charAt(0).toUpperCase() + perm.slice(1)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoleDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveRole} variant="contained">
            {selectedRole ? 'Update' : 'Create'} Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;